import {
  ChangeEvent,
  ChangeEventHandler,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useQuery } from 'relay-hooks/lib/useQuery'
import { PrefectureRomas, prefectures } from '@lifedot/constants/prefectures'
import { usePostalCodeToAddress } from '../use-postal-code-to-address'
import { useAddressGeo } from '../use-address-geo'
import { graphql } from 'relay-runtime'
import { useDialogueAreaQuery } from './__generated__/useDialogueAreaQuery.graphql'

type UseDialogueArea = () => {
  handleChangePostalCode: ChangeEventHandler<HTMLInputElement>
  handleChangePrefecture: (e: ChangeEvent<HTMLSelectElement> | string) => void
  handleChangeCity: (e: ChangeEvent<HTMLSelectElement> | string) => void
  cityRef: RefObject<HTMLSelectElement>
  prefectureRef: RefObject<HTMLSelectElement>
  address: ReturnType<typeof usePostalCodeToAddress>['address']
  cities: { label: string; value: string; group: string }[]
  form: {
    postalCode: string
    prefecture: null | { roma: string; name: string }
    city: null | { cityId: string | number; name: string }
    geo: null | { lat: string; lon: string }
  }
}

export const useDialogueArea: UseDialogueArea = () => {
  const [form, setForm] = useState<ReturnType<UseDialogueArea>['form']>({
    postalCode: '',
    prefecture: null,
    city: null,
    geo: null
  })

  const { data } = useQuery<useDialogueAreaQuery>(
    query,
    {
      prefecture: form.prefecture?.roma ?? ''
    },
    { skip: !form.prefecture?.roma }
  )
  const cities = useMemo(
    () =>
      (
        data?.listCitiesByPrefectureRoma?.items?.map(
          ({ cityId, name, kana }) => ({
            label: name,
            value: String(cityId),
            group: kana[0] ?? '',
            kana
          })
        ) ?? []
      ).sort(({ kana: a }, { kana: b }) => a.localeCompare(b)),
    [data?.listCitiesByPrefectureRoma?.items]
  )

  const handleChangePostalCode = useCallback<
    ChangeEventHandler<HTMLInputElement>
  >((e) => {
    setForm((prev) => ({
      ...prev,
      postalCode: e.target.value
    }))
  }, [])
  const handleChangePrefecture = useCallback(
    (e: ChangeEvent<HTMLSelectElement> | string) => {
      setForm((prev) => ({
        ...prev,
        prefecture: {
          name:
            prefectures[
              (typeof e === 'string' ? e : e.target.value) as PrefectureRomas
            ] ?? '',
          roma: typeof e === 'string' ? e : e.target.value
        }
      }))
    },
    []
  )
  const handleChangeCity = useCallback(
    (e: ChangeEvent<HTMLSelectElement> | string) => {
      setForm((prev) => ({
        ...prev,
        city: {
          name:
            cities.find(
              ({ value }) =>
                value === (typeof e === 'string' ? e : e.target.value)
            )?.label ?? '',
          cityId: typeof e === 'string' ? e : e.target.value
        }
      }))
    },
    [cities]
  )
  const prefectureRef = useRef<HTMLSelectElement>(null)
  const cityRef = useRef<HTMLSelectElement>(null)

  const { address } = usePostalCodeToAddress(
    form.postalCode,
    form.postalCode.length >= 7 && form.postalCode.length < 9
  )
  useEffect(() => {
    if (!address?.prefecture_roma) return

    if (prefectureRef.current) {
      prefectureRef.current.value = address.prefecture_roma ?? ''
      handleChangePrefecture(address.prefecture_roma ?? '')
    }
  }, [address, handleChangePrefecture])

  useEffect(() => {
    if (!address?.city_name) return

    const cityId =
      cities.find(({ label }) => label === address.city_name)?.value ?? null

    if (cityRef.current) {
      cityRef.current.value = cityId ?? ''
      handleChangeCity(cityId ?? '')
    }
  }, [address, cities, handleChangeCity])

  const { geo } = useAddressGeo({
    prefecture: form.prefecture?.name ?? '',
    city: form.city?.name ?? '',
    ready: !!form.prefecture && !!form.city
  })
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      geo
    }))
  }, [geo])

  return {
    handleChangePostalCode,
    handleChangePrefecture,
    handleChangeCity,
    cityRef,
    prefectureRef,
    address,
    cities,
    form
  }
}

const query = graphql`
  query useDialogueAreaQuery($prefecture: String!) {
    listCitiesByPrefectureRoma(prefectureRoma: $prefecture, limit: 300) {
      items {
        cityId: id
        name
        kana
      }
    }
  }
`
